import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Link as RouterLink,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom';
import { Box, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { touchPresence } from './api/companions';
import { openTavernEventStream } from './api/events';
import GameShelf from './pages/GameShelf';
import LoginPage from './pages/LoginPage';
import InstallPrompt from './components/pwa/InstallPrompt';
import PushOptInPrompt from './components/pwa/PushOptInPrompt';
import ChatNotificationBridge from './components/chat/ChatNotificationBridge';
import EnvironmentBanner from './components/environment/EnvironmentBanner';
import CookieConsentBanner from './components/privacy/CookieConsentBanner';
import ErrorBoundary from './components/support/ErrorBoundary';
import ScrollToTop from './components/routing/ScrollToTop';
import RouteMemory from './components/routing/RouteMemory';
import ModalScrollLock from './components/routing/ModalScrollLock';
import BrandLogo from './components/branding/BrandLogo';
import AppLayout from './components/tavern/AppLayout';
import ChatPage from './pages/ChatPage';
import DashboardPage from './pages/DashboardPage';
import FriendsPage from './pages/FriendsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import GatheringInvitePage from './pages/GatheringInvitePage';
import LegalPage from './pages/LegalPage';
import NewNightPage from './pages/NewNightPage';
import NightsPage from './pages/NightsPage';
import NoticesPage from './pages/NoticesPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import ProfileEditPage from './pages/ProfileEditPage';
import ProfilePage from './pages/ProfilePage';
import PublicProfilePage from './pages/PublicProfilePage';
import PrivacyDataSettingsPage from './pages/PrivacyDataSettingsPage';
import RegisterPage from './pages/RegisterPage';
import ResendVerificationPage from './pages/ResendVerificationPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SupportPage from './pages/SupportPage';
import TavernMasterToolsPage from './pages/TavernMasterToolsPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { selectIsAuthenticated } from './features/auth/authSlice';
import { ToastProvider } from './components/tavern/ToastProvider';
import {
  shouldRefreshPushSubscription,
  syncGrantedPushSubscription,
} from './utils/pushNotifications';

const AuthenticatedRoute = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

const publicSupportShellSx = {
  minHeight: '100vh',
  py: { xs: 2, md: 4 },
  background:
    'radial-gradient(circle at 12% 10%, rgba(215,160,75,0.22), transparent 26rem), radial-gradient(circle at 88% 72%, rgba(15,76,69,0.14), transparent 32rem)',
};

const supportContentSx = {
  width: '100%',
  maxWidth: 1180,
  mx: 'auto',
  px: { xs: 2, sm: 3, md: 4 },
};

const SupportRoute = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();

  if (isAuthenticated) {
    return (
      <AppLayout>
        <SupportPage />
      </AppLayout>
    );
  }

  return (
    <Box sx={publicSupportShellSx}>
      <Stack
        component="header"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ ...supportContentSx, mb: { xs: 1, md: 2 } }}
      >
        <Box component={RouterLink} to="/" sx={{ color: 'inherit', textDecoration: 'none' }}>
          <BrandLogo emblemSize={52} />
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button component={RouterLink} to="/login" variant="outlined">
            {t('login.signIn')}
          </Button>
          <Button component={RouterLink} to="/register" variant="contained" color="secondary">
            {t('publicProfile.actions.register')}
          </Button>
        </Stack>
      </Stack>
      <Box component="main" sx={supportContentSx}>
        <SupportPage />
      </Box>
    </Box>
  );
};

const NewNightRoute = () => <NewNightPage key="night-wizard:new" />;

const EditNightRoute = () => {
  const { nightId } = useParams();

  return <NewNightPage key={`night-wizard:edit:${nightId || 'unknown'}`} />;
};

const AppRoutes = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/oauth/callback" element={<OAuthCallbackPage />} />
      <Route path="/invite/gathering/night/:nightId" element={<GatheringInvitePage legacy />} />
      <Route path="/invite/gathering/:token" element={<GatheringInvitePage />} />
      <Route path="/resend-verification" element={<ResendVerificationPage />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/privacy/contact" element={<LegalPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route
        path="/u/:username"
        element={
          <AuthenticatedRoute>
            <PublicProfilePage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/dashboard"
        element={
          <AuthenticatedRoute>
            <DashboardPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/search"
        element={<Navigate to="/gameshelf" replace />}
      />
      <Route
        path="/gameshelf"
        element={
          <AuthenticatedRoute>
            <GameShelf />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/friends"
        element={
          <AuthenticatedRoute>
            <FriendsPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <AuthenticatedRoute>
            <ChatPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/nights"
        element={
          <AuthenticatedRoute>
            <NightsPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/nights/new"
        element={
          <AuthenticatedRoute>
            <NewNightRoute />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/nights/:nightId/edit"
        element={
          <AuthenticatedRoute>
            <EditNightRoute />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <AuthenticatedRoute>
            <ProfilePage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/profile/edit"
        element={
          <AuthenticatedRoute>
            <ProfileEditPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <AuthenticatedRoute>
            <PrivacyDataSettingsPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/settings/privacy"
        element={
          <AuthenticatedRoute>
            <Navigate to="/settings" replace />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/notices"
        element={
          <AuthenticatedRoute>
            <NoticesPage />
          </AuthenticatedRoute>
        }
      />
      <Route
        path="/support"
        element={<SupportRoute />}
      />
      <Route
        path="/settings/tavern-master-tools"
        element={
          <AuthenticatedRoute>
            <TavernMasterToolsPage />
          </AuthenticatedRoute>
        }
      />
    </Routes>
  );
};

const PresenceHeartbeat = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    touchPresence().catch(() => {});
    const intervalId = window.setInterval(() => {
      touchPresence().catch(() => {});
    }, 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [isAuthenticated]);

  return null;
};

const RealtimeEvents = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const source = openTavernEventStream((event) => {
      window.dispatchEvent(new CustomEvent('thetavern:server-event', { detail: event }));
    });

    return () => {
      source?.close();
    };
  }, [isAuthenticated]);

  return null;
};

const StaleDataRevalidation = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let lastDispatchAt = 0;
    const dispatchRevalidate = (reason) => {
      const now = Date.now();
      if (now - lastDispatchAt < 800) return;
      lastDispatchAt = now;
      window.dispatchEvent(
        new CustomEvent('thetavern:revalidate', {
          detail: { reason },
        })
      );
    };

    const handleFocus = () => dispatchRevalidate('focus');
    const handlePageShow = () => dispatchRevalidate('pageshow');
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        dispatchRevalidate('visibility');
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  return null;
};

const PushSubscriptionSync = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let active = true;
    const syncIfNeeded = () => {
      if (!active || !shouldRefreshPushSubscription()) return;

      syncGrantedPushSubscription().catch(() => {});
    };

    syncIfNeeded();
    window.addEventListener('focus', syncIfNeeded);
    document.addEventListener('visibilitychange', syncIfNeeded);
    const handleServiceWorkerMessage = (event) => {
      if (event.data?.type === 'PUSH_SUBSCRIPTION_CHANGED') {
        syncGrantedPushSubscription().catch(() => {});
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      active = false;
      window.removeEventListener('focus', syncIfNeeded);
      document.removeEventListener('visibilitychange', syncIfNeeded);
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [isAuthenticated]);

  return null;
};

const AuthenticatedInstallPrompt = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  return isAuthenticated ? <InstallPrompt /> : null;
};

const App = () => {
  useEffect(() => {
    fetch('/health')
      .then((res) => res.text())
      .then((data) => console.log('Backend response:', data))
      .catch((err) => console.error('Backend connection error:', err));
  }, []);

  return (
    <Router>
      <ToastProvider>
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
          <EnvironmentBanner />
          <PresenceHeartbeat />
          <RealtimeEvents />
          <ChatNotificationBridge />
          <StaleDataRevalidation />
          <PushSubscriptionSync />
          <RouteMemory />
          <ModalScrollLock />
          <ScrollToTop />
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
          <CookieConsentBanner />
          <AuthenticatedInstallPrompt />
          <PushOptInPrompt />
        </Box>
      </ToastProvider>
    </Router>
  );
};

export default App;
